import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './Kpi.module.css'

export type KpiTone = 'ok' | 'bad'

export interface KpiProps {
  /** Ce que l'indicateur mesure — « Attribués (équipe) », « Avancement ». */
  label: ReactNode
  /**
   * La valeur, telle qu'elle s'affiche : c'est l'appelant qui met en forme.
   *
   * ⚠️ **Un indicateur sans source passe un tiret cadratin, pas zéro.** Zéro est une mesure, et
   * l'afficher pour « on ne sait pas encore compter ça » est le pire des deux — c'est le cas de
   * « Cette semaine », que `TeamLoad` ne borne pas encore dans le temps.
   */
  value: ReactNode
  /**
   * Précision sous le libellé — la raison d'une valeur absente, la borne d'un comptage.
   *
   * ⚠️ **Absent n'est pas vide** : sans `note`, la carte n'en affiche aucune. Une carte qui
   * réserverait la place d'une précision inexistante décalerait les autres de la rangée.
   */
  note?: ReactNode
  /**
   * Le ton de statut de la valeur — le récapitulatif d'import du prototype (`importOcrRecap`) colore
   * « Prêts à importer » en vert et « Doublons à résoudre » en rouge. Absent, la valeur reste neutre.
   */
  tone?: KpiTone
  /** Ce qui se pose sous la valeur — une `ProgressBar` pour un pourcentage. */
  children?: ReactNode
  className?: string
}

/**
 * Carte d'indicateur — le `.kpi` du prototype validé : un nombre, son libellé, et ce que la
 * carte ajoute pour se qualifier.
 *
 * L'ordre du balisage est **valeur puis libellé**, comme le prototype : c'est le nombre qu'on
 * lit d'abord dans une rangée de trois.
 *
 * ⚠️ **Ce n'est pas une `dl`, et l'appelant ne doit pas en faire une.** Une rangée d'indicateurs
 * n'est pas une liste de définitions : le libellé n'y définit pas la valeur, il la nomme. Les
 * fiches équipe et membre l'apprennent à leurs dépens — leur première version emboîtait des
 * `<dd>` dans un `<dl>`, ce qui rendait la rangée illisible au lecteur d'écran comme à l'œil.
 */
export function Kpi({ label, value, note, tone, children, className }: KpiProps) {
  return (
    <div className={cn(s.root, className)}>
      <span className={s.value} data-tone={tone}>
        {value}
      </span>
      <span className={s.label}>{label}</span>
      {note !== undefined && <span className={s.note}>{note}</span>}
      {children !== undefined && <div className={s.footer}>{children}</div>}
    </div>
  )
}
